/**
 * Chutt... - Main Layout (Luxury Refonte)
 * FloatingNav + pleine largeur — plus de sidebar/appbar
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, useMediaQuery, useTheme } from '@mui/material';

import FloatingNav from './FloatingNav';
import Footer from './Footer';
import ScrollToTopFAB from '../shared/ScrollToTopFAB';
import CobrandHeader from '../cockpit/CobrandHeader';
import { monitoringAPI, crisisAPI, agencyAPI, authAPI } from '../../services/api';
import { invalidateCache } from '../../services/cache';
import { radarAPI } from '../../services/apiV2';
import { colors } from '../../styles/colors';
import useFreshnessCheck from '../../hooks/useFreshnessCheck';

const ALL_PAGES = ['radar', 'comprendre', 'agir', 'mesurer'];

function MainLayout({ setAuth }) {
  const navigate = useNavigate();
  const location = useLocation();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(() => {
    const saved = localStorage.getItem('selectedBrandId');
    return saved ? parseInt(saved, 10) : null;
  });
  const [hasCrisis, setHasCrisis] = useState(false);
  const [hasAlerts, setHasAlerts] = useState(false);
  const [crisisData, setCrisisData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [coldStart, setColdStart] = useState(false);
  const [agency, setAgency] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [brandPlan, setBrandPlan] = useState(null);
  const [isSuperadmin, setIsSuperadmin] = useState(false);
  const [agencyLoaded, setAgencyLoaded] = useState(false);
  const [userName, setUserName] = useState('');
  const [userPages, setUserPages] = useState(null); // effective pages for current user + brand

  // CRON freshness check — detects when backend has newer data than localStorage
  const { lastUpdated, cacheVersion } = useFreshnessCheck(selectedBrand);

  // Shared filter preferences
  const [periodDays, setPeriodDays] = useState(() => {
    const saved = localStorage.getItem('chutt_periodDays');
    return saved ? parseInt(saved, 10) : 7;
  });
  const [cardCompact, setCardCompact] = useState(() => {
    const saved = localStorage.getItem('chutt_cardCompact');
    return saved !== null ? saved === 'true' : true;
  });

  useEffect(() => {
    localStorage.setItem('chutt_periodDays', periodDays.toString());
  }, [periodDays]);

  useEffect(() => {
    localStorage.setItem('chutt_cardCompact', cardCompact.toString());
  }, [cardCompact]);

  const [quotaMessage, setQuotaMessage] = useState(null);

  useEffect(() => {
    const handler = () => setColdStart(true);
    window.addEventListener('api:coldstart', handler);
    return () => window.removeEventListener('api:coldstart', handler);
  }, []);

  useEffect(() => {
    const handler = (e) => setQuotaMessage(e.detail?.message || 'Limite quotidienne atteinte.');
    window.addEventListener('api:quota_exceeded', handler);
    return () => window.removeEventListener('api:quota_exceeded', handler);
  }, []);

  // Fetch agency info + user name on mount (co-branding)
  useEffect(() => {
    agencyAPI.getMe()
      .then(res => {
        const data = res.data || res;
        // agencyAPI.getMe() returns flat object: { id, name, logo_url, accent_color, ..., role, is_superadmin }
        const { role, is_superadmin, ...agencyData } = data || {};
        setAgency(agencyData?.id ? agencyData : null);
        setUserRole(role || null);
        setIsSuperadmin(!!is_superadmin);
        setAgencyLoaded(true);
      })
      .catch(() => {
        // No agency (solo user or not yet set up)
        setAgency(null);
        setUserRole(null);
        setIsSuperadmin(false);
        setAgencyLoaded(true);
      });
    // Fetch user profile for display name
    authAPI.me()
      .then(res => {
        const data = res.data || res;
        setUserName(data?.full_name || data?.email || '');
      })
      .catch(() => setUserName(''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedBrand) {
      localStorage.setItem('selectedBrandId', selectedBrand.toString());
    }
  }, [selectedBrand]);

  // Redirect to settings only for non-superadmin with no brands
  useEffect(() => {
    if (agencyLoaded && !isSuperadmin && !loading && brands.length === 0) {
      navigate('/settings');
    }
  }, [agencyLoaded, isSuperadmin, loading, brands, navigate]);

  // Admin landing page: redirect to cockpit on first load
  useEffect(() => {
    if (agencyLoaded && userRole === 'admin_agence' && !isSuperadmin && location.pathname === '/radar') {
      navigate('/dashboard-agence', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agencyLoaded, userRole]);

  // Fetch brand plan when selectedBrand changes — SKIP for superadmin
  useEffect(() => {
    if (isSuperadmin) return;
    if (!selectedBrand) {
      setBrandPlan(null);
      return;
    }
    agencyAPI.getBrandPlan(selectedBrand)
      .then(res => {
        const data = res.data || res;
        setBrandPlan(data);
      })
      .catch(() => {
        setBrandPlan({ pages: ALL_PAGES, missions: ['protect', 'visibility', 'trust'], tier: 'pro', report_frequency: 'hebdomadaire', price_monthly: 100 });
      });
  }, [selectedBrand, isSuperadmin]);

  // Fetch user's effective pages for the selected brand (for non-admin users)
  useEffect(() => {
    if (isSuperadmin) { setUserPages(null); return; }
    if (!selectedBrand || !agencyLoaded) { setUserPages(null); return; }
    // Admin gets null (= all pages from brandPlan)
    if (userRole === 'admin_agence') { setUserPages(null); return; }
    agencyAPI.getMyPages(selectedBrand)
      .then(res => {
        const data = res.data || res;
        setUserPages(data.pages || []);
      })
      .catch(() => setUserPages(null));
  }, [selectedBrand, userRole, isSuperadmin, agencyLoaded]);

  // Effective pages: intersection of brandPlan.pages and userPages (for non-admin)
  const effectivePages = userPages !== null ? userPages : (brandPlan?.pages || null);

  // Redirect if current page is not in effective allowed pages — SKIP for superadmin
  useEffect(() => {
    if (isSuperadmin) return;
    if (!effectivePages || effectivePages.length === 0) return;
    const currentPage = ALL_PAGES.find(p => location.pathname.startsWith(`/${p}`));
    if (currentPage && !effectivePages.includes(currentPage)) {
      navigate(`/${effectivePages[0]}`, { replace: true });
    }
  }, [effectivePages, location.pathname, navigate, isSuperadmin]);

  // Fetch brands — SKIP for superadmin (no brand management)
  const fetchBrands = useCallback(async () => {
    if (isSuperadmin) {
      setLoading(false);
      return [];
    }
    try {
      const res = await monitoringAPI.getBrands();
      const brandsList = res.data.brands || [];
      setBrands(brandsList);

      const savedBrandId = localStorage.getItem('selectedBrandId');
      const savedBrand = savedBrandId ? parseInt(savedBrandId, 10) : null;

      if (brandsList.length > 0) {
        const brandExists = brandsList.some(b => b.id === savedBrand);
        if (brandExists && savedBrand) {
          setSelectedBrand(savedBrand);
        } else {
          setSelectedBrand(brandsList[0].id);
        }
      } else {
        setSelectedBrand(null);
      }
      return brandsList;
    } catch (err) {
      console.error('Error fetching brands:', err);
      return [];
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperadmin]);

  useEffect(() => {
    if (!agencyLoaded) return;
    fetchBrands();
  }, [fetchBrands, agencyLoaded]);

  // Fetch crisis data — SKIP for superadmin
  const fetchCrisisData = useCallback(async () => {
    if (isSuperadmin) return;
    if (!selectedBrand) {
      setCrisisData(null);
      setHasCrisis(false);
      setHasAlerts(false);
      return;
    }
    try {
      const res = await crisisAPI.getCurrent(selectedBrand);
      setCrisisData(res.data);
      setHasCrisis(res.data?.has_crisis || false);
    } catch (err) {
      console.error('Error checking crisis:', err);
      setCrisisData(null);
      setHasCrisis(false);
    }
    try {
      const alertsRes = await radarAPI.getAlerts(selectedBrand);
      const alertsList = alertsRes.data?.alerts || [];
      setHasAlerts(alertsList.length > 0);
    } catch (err) {
      setHasAlerts(false);
    }
  }, [selectedBrand, isSuperadmin]);

  useEffect(() => {
    fetchCrisisData();
  }, [fetchCrisisData]);

  const handleBrandChange = (brandId) => {
    setSelectedBrand(brandId);
  };

  const handleLogout = () => {
    // Clear auth
    localStorage.removeItem('token');
    localStorage.removeItem('selectedBrandId');
    // Clear ALL caches to prevent cross-account data leaks
    invalidateCache();
    // Clear localStorage page caches (radar_data_*, comprendre_data_*, etc.)
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('radar_') || key.startsWith('comprendre_') || key.startsWith('diag_cache_'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(k => localStorage.removeItem(k));
    setAuth(false);
    navigate('/login');
  };

  if (loading || !agencyLoaded) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          bgcolor: colors.ivory,
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <CircularProgress size={32} sx={{ color: colors.primary }} />
        <Typography variant="caption" sx={{ color: colors.textMed }}>
          {coldStart ? 'Le serveur se reveille, patientez (~30s)...' : 'Chargement...'}
        </Typography>
      </Box>
    );
  }

  // ─── SUPERADMIN: only show /superadmin, no nav, no header, nothing else ───
  if (isSuperadmin) {
    if (location.pathname !== '/superadmin') {
      return <Navigate to="/superadmin" replace />;
    }
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: colors.ivory }}>
        <Box component="main" sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, sm: 3, md: 7.5 }, minHeight: '100vh' }}>
          <Outlet context={{ agency, userRole, isSuperadmin, onLogout: handleLogout }} />
        </Box>
      </Box>
    );
  }

  // Show CobrandHeader for admin_agence with agency info
  const showCobrandHeader = userRole === 'admin_agence' && !!agency;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: colors.ivory,
        position: 'relative',
      }}
    >
      {/* Co-branding header bar — visible for admin_agence on all pages */}
      {showCobrandHeader && (
        <CobrandHeader
          agencyName={agency?.name || ''}
          agencyLogoUrl={agency?.logo_url || ''}
          accentColor={agency?.accent_color || colors.primary}
          userName={userName}
          isSuperadmin={isSuperadmin}
          onLogout={handleLogout}
        />
      )}

      <FloatingNav
        brands={brands}
        selectedBrand={selectedBrand}
        onBrandChange={handleBrandChange}
        hasCrisis={hasCrisis}
        hasAlerts={hasAlerts}
        onLogout={handleLogout}
        agency={agency}
        userRole={userRole}
        brandPlan={effectivePages ? { ...brandPlan, pages: effectivePages } : brandPlan}
        isSuperadmin={isSuperadmin}
        hasCobrandHeader={showCobrandHeader}
      />

      {/* Page Content — full width, top padding for floating nav (+ cobrand header offset) */}
      <Box
        component="main"
        sx={{
          pt: isMobile
            ? '64px'
            : (showCobrandHeader ? '120px' : '80px'),
          pb: isMobile ? 'calc(80px + env(safe-area-inset-bottom, 0px))' : 0,
          maxWidth: '1400px',
          mx: 'auto',
          px: { xs: 2, sm: 3, md: 7.5 },
          minHeight: '100vh',
          position: 'relative',
        }}
      >
        <Outlet context={{
          selectedBrand,
          setSelectedBrand,
          brands,
          hasCrisis,
          hasAlerts,
          setHasCrisis,
          crisisData,
          refreshCrisis: fetchCrisisData,
          refreshBrands: fetchBrands,
          profileType: brands?.find(b => b.id === selectedBrand)?.profile_type || 'organisation',
          periodDays,
          setPeriodDays,
          cardCompact,
          setCardCompact,
          agency,
          userRole,
          brandPlan,
          cacheVersion,
          lastUpdated,
        }} />
      </Box>

      <Footer />
      <ScrollToTopFAB />

      {/* Watermark logo — bottom right */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          opacity: 0.03,
          pointerEvents: 'none',
          zIndex: 0,
          fontFamily: '"Dancing Script", cursive',
          fontSize: '6rem',
          fontWeight: 700,
          color: colors.primary,
        }}
      >
        Chutt...
      </Box>

      {/* Quota exceeded notification */}
      {quotaMessage && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 80,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1500,
            maxWidth: 480,
            width: '90%',
          }}
        >
          <Box
            sx={{
              bgcolor: colors.charcoal,
              color: colors.ivory,
              borderRadius: '16px',
              px: 3,
              py: 2,
              fontFamily: '"DM Sans", sans-serif',
              fontSize: '0.85rem',
              lineHeight: 1.5,
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <span>{quotaMessage}</span>
            <Box
              component="button"
              onClick={() => setQuotaMessage(null)}
              sx={{
                background: 'none',
                border: 'none',
                color: colors.textLight,
                cursor: 'pointer',
                fontSize: '1.1rem',
                fontFamily: 'inherit',
                flexShrink: 0,
                '&:hover': { color: colors.ivory },
              }}
            >
              &times;
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default MainLayout;
